import React, { useState, useEffect } from 'react';
import { getCurrentUser } from 'aws-amplify/auth';
import { get, post } from 'aws-amplify/api';
import './Settings.css';

interface SettingsProps {}

interface SFTPCredentials {
  username: string;
  password: string;
  server: string;
  port: number;
}

interface ServerStatus {
  state: 'running' | 'stopped' | 'pending' | 'stopping' | 'starting';
  instanceId: string;
}

export const Settings: React.FC<SettingsProps> = () => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [sftpCredentials, setSftpCredentials] = useState<SFTPCredentials | null>(null);
  const [sftpLoading, setSftpLoading] = useState(false);
  const [serverStatus, setServerStatus] = useState<ServerStatus | null>(null);
  const [serverLoading, setServerLoading] = useState(false);
  const [notifications, setNotifications] = useState({
    uploadComplete: true,
    weeklyDigest: true,
    storageWarnings: true,
  });

  useEffect(() => {
    loadUser();
    loadPreferences();
  }, []);

  const loadUser = async () => {
    try {
      const currentUser = await getCurrentUser();

      // Get email from JWT token claims
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const email = session.tokens?.idToken?.payload?.email as string;

      setUser({
        ...currentUser,
        email: email || currentUser.signInDetails?.loginId || currentUser.username
      });

      // Try to load existing SFTP credentials
      await loadSFTPCredentials(currentUser.userId, email);

      // Check server status if credentials exist
      await checkServerStatus();
    } catch (error) {
      console.error('Failed to load user:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPreferences = () => {
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';
    setDarkMode(savedDarkMode);

    const savedNotifications = localStorage.getItem('notifications');
    if (savedNotifications) {
      setNotifications(JSON.parse(savedNotifications));
    }
  };

  const handleDarkModeToggle = () => {
    const newValue = !darkMode;
    setDarkMode(newValue);
    localStorage.setItem('darkMode', String(newValue));

    // Apply to document
    if (newValue) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const handleNotificationChange = (key: string) => {
    const newNotifications = {
      ...notifications,
      [key]: !notifications[key as keyof typeof notifications],
    };
    setNotifications(newNotifications);
    localStorage.setItem('notifications', JSON.stringify(newNotifications));
  };

  const loadSFTPCredentials = async (userId: string, email: string) => {
    try {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      // Try to get existing credentials from backend
      const restOperation = get({
        apiName: 'PhotosynthesizeAPI',
        path: '/sftp/credentials',
        options: {
          headers: {
            Authorization: token ? `Bearer ${token}` : ''
          }
        }
      });

      const { body } = await restOperation.response;
      const response = await body.json() as any;

      if (response.credentials) {
        setSftpCredentials(response.credentials);
      }
    } catch (error: any) {
      // If 404, credentials don't exist yet - that's ok
      if (error?.response?.statusCode !== 404) {
        console.error('Failed to load SFTP credentials:', error);
      }
    }
  };

  const provisionSFTPCredentials = async () => {
    if (!user?.email || !user?.userId) {
      alert('User information not available');
      return;
    }

    setSftpLoading(true);
    try {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      const restOperation = post({
        apiName: 'PhotosynthesizeAPI',
        path: '/sftp/provision',
        options: {
          headers: {
            Authorization: token ? `Bearer ${token}` : ''
          },
          body: {
            userId: user.userId,
            email: user.email
          }
        }
      });

      const { body } = await restOperation.response;
      const response = await body.json() as any;

      if (response.success && response.credentials) {
        setSftpCredentials(response.credentials);
        alert('SFTP credentials created successfully! Make sure to save your password - it will only be shown once.');
      } else {
        throw new Error(response.error || 'Failed to provision SFTP credentials');
      }
    } catch (error) {
      console.error('Failed to provision SFTP credentials:', error);
      alert('Failed to create SFTP credentials. Please try again or contact support.');
    } finally {
      setSftpLoading(false);
    }
  };

  const startSFTPServer = async () => {
    setServerLoading(true);
    try {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      const restOperation = post({
        apiName: 'PhotosynthesizeAPI',
        path: '/sftp/start',
        options: {
          headers: {
            Authorization: token ? `Bearer ${token}` : ''
          }
        }
      });

      const { body } = await restOperation.response;
      const response = await body.json() as any;

      if (response.success) {
        // Update status to show server is starting
        setServerStatus({
          state: 'starting',
          instanceId: response.instanceId || ''
        });

        // Poll for running state
        setTimeout(() => checkServerStatus(), 5000);
      } else {
        throw new Error(response.error || 'Failed to start SFTP server');
      }
    } catch (error) {
      console.error('Failed to start SFTP server:', error);
      alert('Failed to start SFTP server. Please try again.');
    } finally {
      setServerLoading(false);
    }
  };

  const checkServerStatus = async () => {
    try {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      const restOperation = get({
        apiName: 'PhotosynthesizeAPI',
        path: '/sftp/status',
        options: {
          headers: {
            Authorization: token ? `Bearer ${token}` : ''
          }
        }
      });

      const { body } = await restOperation.response;
      const response = await body.json() as any;

      if (response.status) {
        setServerStatus({
          state: response.status.state,
          instanceId: response.status.instanceId
        });
      }
    } catch (error) {
      console.error('Failed to check server status:', error);
    }
  };

  if (loading) {
    return (
      <div className="settings-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <h1>Settings</h1>
      <p className="settings-subtitle">Manage your preferences and integrations</p>

      {/* Camera SFTP Configuration */}
      <div className="settings-section">
        <h2>📷 Camera Upload (SFTP)</h2>
        <p className="section-description">
          Configure your camera to upload photos automatically via SFTP
        </p>

        {sftpCredentials ? (
          <div className="sftp-credentials">
            {/* Server Status Indicator */}
            {serverStatus && (
              <div className="server-status" style={{
                marginBottom: '1rem',
                padding: '0.75rem',
                borderRadius: '8px',
                backgroundColor: serverStatus.state === 'running' ? '#d4edda' :
                               serverStatus.state === 'stopped' ? '#f8d7da' : '#fff3cd',
                border: `1px solid ${serverStatus.state === 'running' ? '#c3e6cb' :
                                    serverStatus.state === 'stopped' ? '#f5c6cb' : '#ffeaa7'}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: serverStatus.state === 'running' ? '#28a745' :
                                   serverStatus.state === 'stopped' ? '#dc3545' : '#ffc107',
                    display: 'inline-block'
                  }}></span>
                  <strong>Server Status:</strong>
                  <span style={{ textTransform: 'capitalize' }}>{serverStatus.state}</span>
                </div>
                {serverStatus.state === 'stopped' && (
                  <button
                    className="btn-primary"
                    onClick={startSFTPServer}
                    disabled={serverLoading}
                    style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                  >
                    {serverLoading ? 'Starting...' : 'Start Server'}
                  </button>
                )}
              </div>
            )}

            <div className="credential-row">
              <span className="credential-label">Server:</span>
              <code className="credential-value">{sftpCredentials.server}</code>
            </div>
            <div className="credential-row">
              <span className="credential-label">Port:</span>
              <code className="credential-value">{sftpCredentials.port}</code>
            </div>
            <div className="credential-row">
              <span className="credential-label">Protocol:</span>
              <code className="credential-value">SFTP</code>
            </div>
            <div className="credential-row">
              <span className="credential-label">Username:</span>
              <code className="credential-value">{sftpCredentials.username}</code>
            </div>
            <div className="credential-row">
              <span className="credential-label">Password:</span>
              <code className="credential-value">
                {showPassword ? sftpCredentials.password : '••••••••••••••••'}
              </code>
              <button
                className="show-password-btn"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>

            <div className="help-text">
              <p>
                <strong>Supported Cameras:</strong> Canon EOS R5, R6, R3, R6 Mark II, and other models with built-in FTP/SFTP
              </p>
              <p>
                <strong>Setup Instructions:</strong> Menu → Network Settings → FTP Transfer → Enter credentials above
              </p>
              <p>
                <strong>Auto-Shutdown:</strong> The server automatically stops after 15 minutes of inactivity to save costs. Click "Start Server" above to wake it up.
              </p>
              <p>
                <strong>Security Note:</strong> Your password is stored securely and can only be viewed once. If you lose it, you'll need to generate new credentials.
              </p>
            </div>

            <button
              className="btn-secondary"
              onClick={provisionSFTPCredentials}
              disabled={sftpLoading}
              style={{ marginTop: '1rem' }}
            >
              {sftpLoading ? 'Generating...' : 'Regenerate Credentials'}
            </button>
          </div>
        ) : (
          <div className="sftp-provision">
            <p>You haven't set up SFTP credentials yet. Click below to generate secure credentials for camera uploads.</p>
            <button
              className="btn-primary"
              onClick={provisionSFTPCredentials}
              disabled={sftpLoading}
            >
              {sftpLoading ? 'Generating Credentials...' : 'Generate SFTP Credentials'}
            </button>
          </div>
        )}
      </div>

      {/* Appearance */}
      <div className="settings-section">
        <h2>🎨 Appearance</h2>

        <div className="setting-item">
          <div className="setting-info">
            <label>Dark Mode</label>
            <p>Use dark theme across the application</p>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={darkMode}
              onChange={handleDarkModeToggle}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>
      </div>

      {/* Notifications */}
      <div className="settings-section">
        <h2>🔔 Notifications</h2>

        <div className="setting-item">
          <div className="setting-info">
            <label>Upload Complete</label>
            <p>Notify when photo uploads finish processing</p>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={notifications.uploadComplete}
              onChange={() => handleNotificationChange('uploadComplete')}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        <div className="setting-item">
          <div className="setting-info">
            <label>Weekly Digest</label>
            <p>Receive weekly summary of your photography stats</p>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={notifications.weeklyDigest}
              onChange={() => handleNotificationChange('weeklyDigest')}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        <div className="setting-item">
          <div className="setting-info">
            <label>Storage Warnings</label>
            <p>Alert when approaching storage quota</p>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={notifications.storageWarnings}
              onChange={() => handleNotificationChange('storageWarnings')}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>
      </div>

      {/* Storage Defaults */}
      <div className="settings-section">
        <h2>💾 Storage</h2>

        <div className="setting-item">
          <div className="setting-info">
            <label>Default Upload Tier</label>
            <p>Storage tier for new uploads</p>
          </div>
          <select className="settings-select">
            <option value="INSTANT">⚡ Instant ($0.023/GB/month)</option>
            <option value="STANDARD">📦 Standard ($0.023/GB/month)</option>
            <option value="ARCHIVE">🗄️ Archive ($0.004/GB/month)</option>
          </select>
        </div>

        <div className="setting-item">
          <div className="setting-info">
            <label>Auto-Archive After</label>
            <p>Automatically move photos to Archive tier</p>
          </div>
          <select className="settings-select">
            <option value="never">Never</option>
            <option value="30">30 days</option>
            <option value="90">90 days</option>
            <option value="180">180 days</option>
            <option value="365">1 year</option>
          </select>
        </div>
      </div>

      {/* Integrations */}
      <div className="settings-section">
        <h2>🔗 Integrations</h2>

        <div className="integration-item">
          <div className="integration-info">
            <h3>Adobe Lightroom Classic</h3>
            <p>Sync flagged photos with Lightroom</p>
          </div>
          <button className="integration-button">
            Configure Plugin
          </button>
        </div>

        <div className="integration-item">
          <div className="integration-info">
            <h3>Webhooks</h3>
            <p>Get notified when events occur</p>
          </div>
          <button className="integration-button">
            Add Webhook
          </button>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="settings-section danger-zone">
        <h2>⚠️ Danger Zone</h2>

        <div className="danger-item">
          <div className="danger-info">
            <h3>Export Data</h3>
            <p>Download all your photos and metadata</p>
          </div>
          <button className="danger-button secondary">
            Export All Data
          </button>
        </div>

        <div className="danger-item">
          <div className="danger-info">
            <h3>Delete Account</h3>
            <p>Permanently delete your account and all data</p>
          </div>
          <button className="danger-button primary">
            Delete Account
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
